import type { IFormProtectionConfig, TCaptchaProvider } from "@nvb/ui";

/**
 * Build the site-wide form-protection config from Vite env vars.
 * Returns `null` to disable site-wide protection — individual <Form>
 * components can still opt in via `data.protection`.
 *
 * Env vars (frontend, exposed to the browser):
 *   VITE_FORM_PROTECTION_ENABLED   — "true" to enable
 *   VITE_CAPTCHA_PROVIDER          — "turnstile" | "recaptcha" (optional)
 *   VITE_CAPTCHA_SITE_KEY          — provider site key
 *   VITE_CAPTCHA_ACTION            — reCAPTCHA v3 action name (optional)
 */
export const getSiteFormProtectionConfig = (): IFormProtectionConfig | null => {
	// Every variable is read as a full `import.meta.env.VITE_*` member: Vite
	// replaces that form statically, while vike turns a bare `import.meta.env`
	// into `null` (https://vike.dev/env).
	const enabled = (import.meta.env.VITE_FORM_PROTECTION_ENABLED || "").toLowerCase() === "true";
	if (!enabled) return null;

	const rawProvider = (import.meta.env.VITE_CAPTCHA_PROVIDER || "").toLowerCase();
	const provider: TCaptchaProvider | null =
		rawProvider === "turnstile" || rawProvider === "recaptcha" ? rawProvider : null;

	const siteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || "";

	// Honeypot + timestamp work without a captcha provider — keep protection
	// enabled even if the captcha key is missing, but skip the captcha block.
	const captcha = provider && siteKey ? { provider, siteKey, action: import.meta.env.VITE_CAPTCHA_ACTION } : undefined;

	return { enabled: true, ...(captcha ? { captcha } : {}) };
};
